/**
 * Per-step "changed since last visit" counter for re-cert visits.
 *
 * The recert diff engine in `payloadDiff.js` already returns a flat list
 * of every changed field, plus diagnoses/medications/ADL deltas. This
 * helper buckets those changes by wizard step using `fieldToStep` so
 * each step in the sidebar can render a chip like "5 changed".
 *
 * Returns `{1: {count, items[]}, 2: {count, items[]}, ..., total}` so a
 * Sidebar `.map` can index by step number with no defensive checks AND
 * the per-step popover can show the actual list of what changed.
 */

import { diffAgainstSnapshot } from "@/lib/payloadDiff";
import { fieldToStep } from "@/lib/fieldToStep";

const RECERT_SOURCE_KEY = "wi-pa-copy-source-v1";

export function loadRecertBaseline() {
  try {
    const raw = window.localStorage.getItem(RECERT_SOURCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.snapshot || null;
  } catch {
    return null;
  }
}

function emptyBuckets() {
  const out = Object.fromEntries(
    Array.from({ length: 11 }, (_, i) => [i, { count: 0, items: [] }])
  );
  out.total = 0;
  return out;
}

function pushItem(buckets, step, item) {
  if (!buckets[step]) buckets[step] = { count: 0, items: [] };
  buckets[step].items.push(item);
  buckets[step].count += 1;
}

/** Format a single value compactly for display in the popover. */
function fmtValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  const s = String(v).trim();
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
}

export function recertChangesByStep(currentData) {
  const buckets = emptyBuckets();
  const baseline = loadRecertBaseline();
  if (!baseline || !currentData) return buckets;

  const diff = diffAgainstSnapshot(currentData, baseline);
  if (!diff || diff.totalChanges === 0) return buckets;

  // Plain field paths via fieldToStep.
  for (const f of diff.fields || []) {
    const head = (f.path || "").split(".")[0];
    const mapped = fieldToStep(head);
    if (!mapped) continue;
    pushItem(buckets, mapped.step, {
      kind: "field",
      label: f.label || f.path,
      path: f.path,
      before: fmtValue(f.before),
      after: fmtValue(f.after),
    });
  }

  // Diagnoses → Step 5
  for (const dx of diff.diagnoses?.added || []) {
    pushItem(buckets, 5, {
      kind: "diagnosis_added",
      label: `+ ${dx.icd10 || ""} ${dx.description || ""}`.trim(),
    });
  }
  for (const dx of diff.diagnoses?.removed || []) {
    pushItem(buckets, 5, {
      kind: "diagnosis_removed",
      label: `− ${dx.icd10 || ""} ${dx.description || ""}`.trim(),
    });
  }
  // Medications → Step 5
  for (const m of diff.medications?.added || []) {
    pushItem(buckets, 5, {
      kind: "medication_added",
      label: `+ ${m.name || ""} ${m.dose || ""}`.trim(),
    });
  }
  for (const m of diff.medications?.removed || []) {
    pushItem(buckets, 5, {
      kind: "medication_removed",
      label: `− ${m.name || ""}`.trim(),
    });
  }
  for (const c of diff.medications?.changed || []) {
    const before = `${c.before?.dose || ""} ${c.before?.frequency || ""}`.trim();
    const after = `${c.after?.dose || ""} ${c.after?.frequency || ""}`.trim();
    pushItem(buckets, 5, {
      kind: "medication_changed",
      label: c.after?.name || "",
      before,
      after,
    });
  }
  // ADL → Step 2
  for (const a of diff.adls || []) {
    pushItem(buckets, 2, {
      kind: "adl",
      label: `${a.key} pcst level`,
      before: fmtValue(a.before),
      after: fmtValue(a.after),
    });
  }

  buckets.total = diff.totalChanges;
  return buckets;
}

export const RECERT_BASELINE_KEY = RECERT_SOURCE_KEY;
